import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Stack,
  Paper,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  CheckCircle,
  RadioButtonUnchecked,
  ArrowForward,
  Person,
  AccessTime,
  Note,
} from '@mui/icons-material';
import { getOrderStatusHistory, getUserById } from '../../api/api';

const siteBlue = '#002F87';

const formatDateTime = (dateString) => {
  if (!dateString) return 'Unknown (GMT +4)';
  const date = new Date(dateString);
  
  // Timestamp is already stored in GMT+4 from backend, just format it
  // No timezone conversion needed - display as-is
  const formatted = date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  
  return `${formatted} (GMT +4)`;
};

// Use a single site brand color throughout the timeline
const getStatusColor = () => siteBlue;

const StatusChangeItem = ({ item, isLast }) => {
  const isStatusChange = item.change_type === 'STATUS_CHANGE' || (!item.change_type && item.old_status && item.new_status && item.old_status !== item.new_status); // backward compatibility
  const isNoteOnly = item.change_type === 'NOTE_ONLY' || (!item.change_type && item.old_status && item.new_status && item.old_status === item.new_status && item.notes); // backward compatibility for note-only
  const isItemUpdate = item.change_type === 'ITEM_UPDATE';
  const isInitial = isStatusChange && !item.old_status;
  const statusColor = getStatusColor();
  
  // Format field name for display
  const formatFieldName = (field) => {
    const map = {
      'quantity': 'Quantity',
      'unit_price': 'Unit Price',
      'snapshot_product_cost_price': 'Unit Cost Price',
      'availability': 'Availability'
    };
    return map[field] || field;
  };
  
  // Get display heading for item updates - prefer notes if available, otherwise use formatted field name
  const getItemUpdateHeading = (item) => {
    if (item.notes && (item.notes.includes('Retail Price') || item.notes.includes('Cost Price') || item.notes.includes('Price Changed'))) {
      return item.notes;
    }
    return `${formatFieldName(item.field_name)} Changed`;
  };
  
  return (
    <TimelineItem>
      <TimelineOppositeContent
        sx={{ m: 'auto 0', minWidth: '120px' }}
        align="right"
        variant="body2"
        color="text.secondary"
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          <AccessTime fontSize="small" />
          {formatDateTime(item.created_at)}
        </Box>
      </TimelineOppositeContent>
      
      <TimelineSeparator>
        <TimelineDot
          sx={{
            bgcolor: statusColor,
            color: 'white',
            border: '2px solid white',
            boxShadow: '0 0 0 2px ' + statusColor,
          }}
        >
          {isStatusChange ? (isInitial ? <CheckCircle /> : <ArrowForward />) : <CheckCircle />}
        </TimelineDot>
        {!isLast && <TimelineConnector />}
      </TimelineSeparator>
      
      <TimelineContent sx={{ py: '12px', px: 2 }}>
        <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50', borderLeft: `3px solid ${siteBlue}` }}>
          <Stack spacing={1}>
            {isStatusChange && (
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                {isInitial ? (
                  <Typography variant="h6" color="primary">
                    Order Created
                  </Typography>
                ) : (
                  <>
                    <Chip
                      label={item.old_status}
                      size="small"
                      variant="outlined"
                      sx={{ textDecoration: 'line-through', opacity: 0.7, borderColor: siteBlue, color: siteBlue }}
                    />
                    <ArrowForward fontSize="small" color="action" />
                    <Chip
                      label={item.new_status}
                      size="small"
                      sx={{
                        bgcolor: '#ffffff',
                        color: siteBlue,
                        borderColor: siteBlue,
                        borderWidth: 1,
                        borderStyle: 'solid',
                        fontWeight: 700,
                      }}
                    />
                  </>
                )}
              </Box>
            )}
            
            {isNoteOnly && (
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Chip
                  label={item.new_status || item.old_status}
                  size="small"
                  sx={{
                    bgcolor: '#ffffff',
                    color: siteBlue,
                    borderColor: siteBlue,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    fontWeight: 700,
                  }}
                />
              </Box>
            )}
            
            {isItemUpdate && (
              <Box>
                {/* Product Info: SKU and Description */}
                {(item.product_sku || item.product_description) && (
                  <Box sx={{ mb: 1.5, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                    {item.product_description && (
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', mb: 0.5 }}>
                        {item.product_description}
                      </Typography>
                    )}
                    {item.product_sku && (
                      <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                        SKU: {item.product_sku}
                      </Typography>
                    )}
                  </Box>
                )}
                
                {/* Field Change Display */}
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="body1" sx={{ fontWeight: 600, color: siteBlue }}>
                    {getItemUpdateHeading(item)}
                  </Typography>
                  <Chip
                    label={item.old_value}
                    size="small"
                    variant="outlined"
                    sx={{ textDecoration: 'line-through', opacity: 0.7, borderColor: siteBlue, color: siteBlue }}
                  />
                  <ArrowForward fontSize="small" color="action" />
                  <Chip
                    label={item.new_value}
                    size="small"
                    sx={{
                      bgcolor: '#ffffff',
                      color: siteBlue,
                      borderColor: siteBlue,
                      borderWidth: 1,
                      borderStyle: 'solid',
                      fontWeight: 700,
                    }}
                  />
                </Box>
              </Box>
            )}
            
            <Box display="flex" alignItems="center" gap={1}>
              <Person fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Changed by: <strong>{item.changed_by_username}</strong>
              </Typography>
            </Box>
            
            {item.notes && !isItemUpdate && (
              <Box display="flex" alignItems="flex-start" gap={1}>
                <Note fontSize="small" color="action" sx={{ mt: 0.5 }} />
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  "{item.notes}"
                </Typography>
              </Box>
            )}
          </Stack>
        </Paper>
      </TimelineContent>
    </TimelineItem>
  );
};

export default function StatusHistory({ orderId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }

    const fetchHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getOrderStatusHistory(orderId);

        // Enrich changer display with username (email) via admin API if backend didn't format it
        const entries = Array.isArray(data) ? data : [];
        const uniqueUserIds = [...new Set(entries.map(e => e.changed_by_user_id).filter(Boolean))];

        const idToDisplay = {};
        if (uniqueUserIds.length) {
          try {
            const users = await Promise.all(uniqueUserIds.map(id => getUserById(id).catch(() => null)));
            users.forEach((u) => {
              if (u && u.id) {
                const name = u.username || 'user';
                const email = u.email || 'unknown@example.com';
                idToDisplay[u.id] = `${name} (${email})`;
              }
            });
          } catch {}
        }

        const normalized = entries.map((e) => ({
          ...e,
          changed_by_username: idToDisplay[e.changed_by_user_id] || e.changed_by_username || 'user',
        }));

        // Reverse to show newest first
        setHistory(normalized.reverse());
      } catch (err) {
        console.error('Error fetching status history:', err);
        setError('Failed to load status history');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [orderId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!history || history.length === 0) {
    return (
      <Card sx={{ m: 2 }}>
        <CardContent>
          <Typography variant="h6" color="text.secondary" textAlign="center">
            No status history available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        Order Status History
      </Typography>
      
      <Timeline position="right">
        {history.map((item, index) => (
          <StatusChangeItem
            key={item.id}
            item={item}
            isLast={index === history.length - 1}
          />
        ))}
      </Timeline>
    </Box>
  );
}
